import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import JiraLinkBanner from './JiraLinkBanner';
import { SidebarContext } from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';
import NotFoundPage from '../pages/NotFoundPage';
import { canAccessPath, isKnownPath, roleOf } from '../utils/roles';

const DESKTOP_QUERY = '(min-width: 1024px)';
const SIDEBAR_KEY = 'jarvis.sidebar.open';

// On a wide screen the menu stays where the person left it, from page to page and across reloads.
// The first time it is the narrow rail of icons; on a phone it is always a drawer that starts closed.
function storedOpen(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_KEY) === '1';
  } catch {
    return false;
  }
}

export default function AppLayout() {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(DESKTOP_QUERY).matches
  );
  // Closed by default (a rail of icons on a wide screen, a hidden drawer on a phone); opened via the header hamburger.
  const [open, setOpenState] = useState(() => isDesktop && storedOpen());
  const setOpen = useCallback(
    (v: boolean) => {
      setOpenState(v);
      if (!isDesktop) return;
      try {
        window.localStorage.setItem(SIDEBAR_KEY, v ? '1' : '0');
      } catch {
        // Private window or blocked storage: the menu simply is not remembered.
      }
    },
    [isDesktop]
  );

  // Guard: a page that exists but is not for this role is never mounted (so it never starts reading data);
  // the person is told, and shown the pages they can open. A path that is no page at all falls to the "*" route.
  const { user } = useAuth();
  const role = roleOf(user);
  const { pathname } = useLocation();
  const blocked = isKnownPath(pathname) && !canAccessPath(role, pathname);

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Escape closes the drawer on a phone; on a wide screen the menu is a pinned panel and stays.
  useEffect(() => {
    if (!open || isDesktop) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, isDesktop, setOpen]);

  const ctx = useMemo(
    () => ({ open, setOpen, toggle: () => setOpen(!open) }),
    [open, setOpen]
  );

  return (
    <SidebarContext.Provider value={ctx}>
      <div className="min-h-screen bg-secondary-50">
        {/* First thing a keyboard reaches: jump past the menu to the page. It is only seen while it has focus. */}
        <a
          href="#main"
          onClick={(e) => {
            e.preventDefault(); // a hash in the address would be read by the pages (the Guide opens words by hash)
            document.getElementById('main')?.focus();
          }}
          className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[1000004] focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-blue-700 focus:shadow-lg focus:ring-2 focus:ring-blue-500"
        >
          Skip to the page
        </a>

        {/* Phone: the open drawer dims the page and closes on a tap outside it. Sidebar (z-50) stays above.
            Wide screen: the menu is always there, as a narrow rail of icons or open with labels, and has no click-away layer. */}
        {open && !isDesktop && (
          <div role="presentation" data-testid="menu-overlay" className="fixed inset-0 z-40 bg-black bg-opacity-50" onClick={() => setOpen(false)} />
        )}

        {/* Wide screen: the rail (4.25rem) or the open menu (16rem). Phone: a drawer that slides in and out. */}
        <aside
          aria-label="Navigation"
          inert={!isDesktop && !open}
          className={
            isDesktop
              ? `fixed inset-y-0 left-0 z-50 overflow-hidden transition-[width] duration-300 ease-in-out motion-reduce:transition-none ${open ? 'w-64' : 'w-[4.25rem]'}`
              : `fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out motion-reduce:transition-none ${open ? 'translate-x-0 shadow-xl' : '-translate-x-full'}`
          }
        >
          <Sidebar collapsed={isDesktop && !open} onNavigate={() => { if (!isDesktop) setOpen(false); }} />
        </aside>

        {/* Content: always clear of the rail on a wide screen, and of the open menu when it is open. */}
        <div data-testid="page-area" className={`transition-[padding] duration-300 ease-in-out motion-reduce:transition-none ${open ? 'lg:pl-64' : 'lg:pl-[4.25rem]'}`}>
          <JiraLinkBanner />
          <main id="main" tabIndex={-1} className="focus:outline-none">
            {blocked ? <NotFoundPage reason="blocked" /> : <Outlet />}
          </main>
        </div>
      </div>
    </SidebarContext.Provider>
  );
}
